import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { AsistenciaZoomPanel } from './AsistenciaZoomPanel';
import { MeetAnfitrionDialog } from './MeetAnfitrionDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Plus, Loader2, Trash2, Video, Clock, Calendar, ExternalLink,
  Edit2, X, MonitorPlay, Film, Search, Link2, MapPin,
  Wand2, PlayCircle,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const tieneAsistenciaAuto = (r) => (r.plataforma === 'zoom' && r.zoom_registro) || (r.plataforma === 'meet' && r.meet_space);

const PLATAFORMA_CONFIG = {
  meet: { label: 'Google Meet', color: 'bg-green-100 text-green-700', virtual: true },
  zoom: { label: 'Zoom', color: 'bg-blue-100 text-blue-700', virtual: true },
  teams: { label: 'Teams', color: 'bg-indigo-100 text-indigo-700', virtual: true },
  presencial: { label: 'Presencial', color: 'bg-amber-100 text-amber-700', virtual: false },
  otro: { label: 'Otro', color: 'bg-gray-100 text-gray-600', virtual: true },
};

const ESTADO_CONFIG = {
  programada: { label: 'Programada', color: 'bg-blue-100 text-blue-700' },
  en_curso: { label: 'En curso', color: 'bg-green-100 text-green-700 animate-pulse' },
  finalizada: { label: 'Finalizada', color: 'bg-gray-100 text-gray-600' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-600' },
};

function formatFecha(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
}

function formatFechaCorta(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function CampusMeetings({ spaceId, esCoordinadorODocente, token, tipoEspacio }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [reuniones, setReuniones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titulo: '', descripcion: '', fecha_hora: '', duracion: 60,
    link_reunion: '', plataforma: 'meet', notas: ''
  });

  // Linked events state
  const [eventosVinculados, setEventosVinculados] = useState([]);
  const [showLinkEvent, setShowLinkEvent] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [eventResults, setEventResults] = useState([]);
  const [searchingEvents, setSearchingEvents] = useState(false);

  const fetchReuniones = useCallback(async () => {
    setLoading(true);
    try {
      const [reunionesRes, eventosRes] = await Promise.all([
        axios.get(`${API_URL}/campus/espacios/${spaceId}/reuniones`, { headers }),
        axios.get(`${API_URL}/campus/espacios/${spaceId}/eventos-vinculados`, { headers })
      ]);
      setReuniones(reunionesRes.data);
      setEventosVinculados(eventosRes.data);
    } catch (err) {
      console.error('Error cargando reuniones:', err);
    } finally {
      setLoading(false);
    }
  }, [spaceId]);

  useEffect(() => { fetchReuniones(); }, [fetchReuniones]);

  const resetForm = () => {
    setForm({ titulo: '', descripcion: '', fecha_hora: '', duracion: 60, link_reunion: '', plataforma: 'meet', notas: '' });
    setShowForm(false);
    setEditingId(null);
  };

  const esVirtual = PLATAFORMA_CONFIG[form.plataforma]?.virtual !== false;
  const admiteAuto = ['meet', 'zoom'].includes(form.plataforma);
  const [generarAuto, setGenerarAuto] = useState(true);
  const [generandoId, setGenerandoId] = useState(null);
  const [meetHost, setMeetHost] = useState(null);
  const { user } = useAuth();

  const generarLink = async (id) => {
    setGenerandoId(id);
    try {
      const res = await axios.post(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}/generar-link`, {}, { headers });
      if (res.data?.aviso) alert(res.data.aviso);
      fetchReuniones();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo generar el link');
    } finally {
      setGenerandoId(null);
    }
  };

  const abrirMiLink = async (id) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}/mi-link`, { headers });
      win.location.href = res.data.join_url;
    } catch (err) {
      win.close();
      alert(err.response?.data?.detail || 'No se pudo obtener tu link');
    }
  };

  const abrirComoAnfitrion = async (id, googleEmail) => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}/link-anfitrion`,
        { headers, params: googleEmail ? { google_email: googleEmail } : undefined });
      win.location.href = res.data.start_url;
      if (res.data.aviso) alert(res.data.aviso);
    } catch (err) {
      win.close();
      alert(err.response?.data?.detail || 'No se pudo obtener el link de anfitrión');
    }
  };

  const iniciarAnfitrion = (item) => {
    if (item.plataforma === 'meet' && item.meet_space) setMeetHost(item);
    else abrirComoAnfitrion(item.id);
  };

  const guardar = async () => {
    if (!form.titulo.trim() || !form.fecha_hora) {
      alert('Completá título y fecha/hora'); return;
    }
    const autoAlGuardar = !editingId && admiteAuto && generarAuto && !form.link_reunion.trim();
    if (esVirtual && !form.link_reunion.trim() && !autoAlGuardar) {
      alert('Completá el link de la reunión virtual'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        duracion: parseInt(form.duracion) || 60,
        fecha_hora: new Date(form.fecha_hora).toISOString()
      };
      if (editingId) {
        const { _auto, ...resto } = payload;
        const res = await axios.put(`${API_URL}/campus/espacios/${spaceId}/reuniones/${editingId}`, resto, { headers });
        if (res.data?.aviso) alert(res.data.aviso);
      } else {
        const res = await axios.post(`${API_URL}/campus/espacios/${spaceId}/reuniones`, { ...payload, generar_link: autoAlGuardar }, { headers });
        if (res.data?.aviso) alert(res.data.aviso);
      }
      resetForm();
      fetchReuniones();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar reunión');
    } finally {
      setSaving(false);
    }
  };

  const editar = (r) => {
    const d = new Date(r.fecha_hora);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({
      titulo: r.titulo, descripcion: r.descripcion || '', fecha_hora: local,
      duracion: r.duracion || 60, link_reunion: r.link_reunion || '',
      plataforma: r.plataforma || 'meet', notas: r.notas || '', _auto: !!r.creada_automaticamente
    });
    setEditingId(r.id);
    setShowForm(true);
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta reunión?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}`, { headers });
      fetchReuniones();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      await axios.put(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}`, { estado }, { headers });
      fetchReuniones();
    } catch (err) {
      alert('Error al cambiar estado');
    }
  };

  const agregarGrabacion = async (id) => {
    const url = prompt('URL de la grabación:');
    if (!url) return;
    try {
      await axios.put(`${API_URL}/campus/espacios/${spaceId}/reuniones/${id}`, { grabacion_url: url }, { headers });
      fetchReuniones();
    } catch (err) {
      alert('Error al guardar grabación');
    }
  };

  // --- Linked Events ---
  const buscarEventos = async (q) => {
    setEventSearch(q);
    if (q.trim().length < 2) { setEventResults([]); return; }
    setSearchingEvents(true);
    try {
      const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/buscar-eventos?q=${encodeURIComponent(q.trim())}`, { headers });
      setEventResults(res.data);
    } catch (err) {
      console.error('Error buscando eventos:', err);
    } finally {
      setSearchingEvents(false);
    }
  };

  const vincularEvento = async (eventoId) => {
    try {
      await axios.post(`${API_URL}/campus/espacios/${spaceId}/eventos-vinculados?evento_id=${eventoId}`, {}, { headers });
      setEventSearch('');
      setEventResults([]);
      setShowLinkEvent(false);
      fetchReuniones();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al vincular');
    }
  };

  const desvincularEvento = async (linkId) => {
    if (!window.confirm('¿Desvincular este evento?')) return;
    try {
      await axios.delete(`${API_URL}/campus/espacios/${spaceId}/eventos-vinculados/${linkId}`, { headers });
      fetchReuniones();
    } catch (err) {
      alert('Error al desvincular');
    }
  };

  // Unify meetings and linked events into one list
  const ahora = new Date();
  
  const itemsUnificados = [
    // Reuniones internas
    ...reuniones.map(r => ({
      ...r,
      _tipo: 'reunion',
      _nombre: r.titulo,
      _fecha: r.fecha_hora,
      _estado: r.estado_calculado || r.estado,
      _duracion: r.duracion,
      _plataforma: r.plataforma,
      _link: r.link_reunion,
      _lugar: null,
    })),
    // Eventos vinculados
    ...eventosVinculados.map(lnk => {
      const ev = lnk.evento || {};
      const fechaEvento = ev.fecha_evento;
      const horaEvento = ev.hora_evento;
      let fechaCompleta = null;
      if (fechaEvento) {
        fechaCompleta = horaEvento ? `${fechaEvento}T${horaEvento}:00` : `${fechaEvento}T00:00:00`;
      }
      let estadoCalc = 'programada';
      if (fechaCompleta) {
        try {
          const d = new Date(fechaCompleta);
          if (ahora > new Date(d.getTime() + 3 * 3600000)) estadoCalc = 'finalizada';
        } catch(e) {}
      }
      return {
        id: lnk.id,
        _tipo: 'evento',
        _nombre: ev.nombre,
        _fecha: fechaCompleta,
        _fechaEvento: fechaEvento,
        _horaEvento: horaEvento,
        _estado: estadoCalc,
        _duracion: null,
        _plataforma: ev.modalidad || 'presencial',
        _link: null,
        _lugar: ev.lugar,
        _eventoId: ev.id,
        _inscripcionesAbiertas: ev.inscripciones_abiertas,
        _descripcion: ev.descripcion_corta,
        _linkId: lnk.id,
        grabacion_url: null,
        autor: null,
      };
    })
  ];

  // Sort by date
  itemsUnificados.sort((a, b) => {
    const da = a._fecha ? new Date(a._fecha) : new Date(0);
    const db2 = b._fecha ? new Date(b._fecha) : new Date(0);
    return da - db2;
  });

  const proximos = itemsUnificados.filter(i => i._estado === 'programada' || i._estado === 'en_curso');
  const anteriores = itemsUnificados.filter(i => i._estado === 'finalizada' || i._estado === 'cancelada');

  return (
    <div className="space-y-4">
      {meetHost && (
        <MeetAnfitrionDialog emailInicial={localStorage.getItem('campus_google_email') || user?.google_email || (/@gmail\.com$/i.test(user?.email || '') ? user.email : '')}
          onCancel={() => setMeetHost(null)}
          onConfirm={(email) => { localStorage.setItem('campus_google_email', email); const id = meetHost.id; setMeetHost(null); abrirComoAnfitrion(id, email); }} />
      )}
      {esCoordinadorODocente && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => { setShowLinkEvent(!showLinkEvent); setShowForm(false); }}
            data-testid="link-event-btn">
            <Link2 className="w-4 h-4 mr-1" /> Vincular evento
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setShowForm(true); setShowLinkEvent(false); }} data-testid="create-meeting-btn">
            <Plus className="w-4 h-4 mr-1" /> Reunión interna
          </Button>
        </div>
      )}

      {/* Link Event Search */}
      {showLinkEvent && esCoordinadorODocente && (
        <Card data-testid="link-event-panel">
          <CardContent className="p-4 space-y-3">
            <h3 className="text-sm font-semibold">Vincular evento existente</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Buscar evento por nombre..." value={eventSearch}
                onChange={(e) => buscarEventos(e.target.value)} className="pl-10"
                data-testid="search-event-input" />
            </div>
            {searchingEvents && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-violet-600" /></div>}
            {!searchingEvents && eventSearch.trim().length >= 2 && eventResults.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">No se encontraron eventos</p>
            )}
            {eventResults.length > 0 && (
              <div className="border rounded-md divide-y max-h-[200px] overflow-y-auto">
                {eventResults.map(ev => (
                  <div key={ev.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50" data-testid={`event-result-${ev.id}`}>
                    <div>
                      <span className="text-sm font-medium">{ev.nombre}</span>
                      <div className="text-xs text-gray-400 flex items-center gap-2">
                        {ev.fecha_evento && <span><Calendar className="w-3 h-3 inline mr-0.5" />{new Date(ev.fecha_evento + 'T12:00:00').toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day:'2-digit', month:'short'})}</span>}
                        {ev.hora_evento && <span><Clock className="w-3 h-3 inline mr-0.5" />{ev.hora_evento} hs</span>}
                        {ev.lugar && <span><MapPin className="w-3 h-3 inline mr-0.5" />{ev.lugar}</span>}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => vincularEvento(ev.id)}
                      data-testid={`link-event-${ev.id}`}>
                      <Link2 className="w-3.5 h-3.5 mr-1" />Vincular
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form */}
      {showForm && (
        <Card data-testid="meeting-form">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? 'Editar Reunión' : 'Nueva Reunión'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Título de la reunión" value={form.titulo}
              onChange={e => setForm({ ...form, titulo: e.target.value })}
              data-testid="meeting-title-input" />
            <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] resize-y"
              placeholder="Descripción o agenda (opcional)" value={form.descripcion}
              onChange={e => setForm({ ...form, descripcion: e.target.value })}
              data-testid="meeting-desc-input" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Fecha y hora *</label>
                <Input type="datetime-local" value={form.fecha_hora}
                  onChange={e => setForm({ ...form, fecha_hora: e.target.value })}
                  className="h-8 text-sm" data-testid="meeting-datetime-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Duración (min)</label>
                <Input type="number" min="15" max="480" value={form.duracion}
                  onChange={e => setForm({ ...form, duracion: e.target.value })}
                  className="h-8 text-sm" data-testid="meeting-duration-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Plataforma</label>
                <select className="w-full border rounded-md px-2 py-1.5 text-sm h-8 disabled:bg-gray-100" value={form.plataforma}
                  disabled={!!(editingId && form._auto)} title={editingId && form._auto ? 'El link se generó automáticamente: no se puede cambiar la plataforma' : undefined}
                  onChange={e => setForm({ ...form, plataforma: e.target.value })}
                  data-testid="meeting-platform-select">
                  <option value="meet">Google Meet</option>
                  <option value="zoom">Zoom</option>
                  <option value="teams">Microsoft Teams</option>
                  <option value="presencial">Presencial</option>
                  <option value="otro">Otro (virtual)</option>
                </select>
              </div>
              <div />
            </div>
            {!editingId && admiteAuto && (
              <label className="flex items-center gap-2 text-sm text-gray-700" data-testid="meeting-auto-label">
                <input type="checkbox" checked={generarAuto} onChange={e => setGenerarAuto(e.target.checked)} data-testid="meeting-auto-checkbox" />
                Generar link de {form.plataforma === 'zoom' ? 'Zoom' : 'Google Meet'} automáticamente al guardar
              </label>
            )}
            {form.plataforma === 'meet' && !(editingId && form._auto) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="meeting-meet-aviso">
                <b>Importante para docentes:</b> en Google Meet los controles de anfitrión solo funcionan si entrás con una cuenta de Google
                (puede ser tu Gmail personal). Al tocar "Iniciar como anfitrión" te pedimos esa cuenta y te damos permisos de coanfitrión.
                Si no tenés cuenta de Google, elegí Zoom: ahí podés ser anfitrión sin cuenta.
              </div>
            )}
            {esVirtual && !(!editingId && admiteAuto && generarAuto) && (
              <div>
                <label className="text-xs font-medium text-gray-600">Link de la reunión *</label>
                <Input placeholder="https://meet.google.com/abc-defg-hij" value={form.link_reunion}
                  onChange={e => setForm({ ...form, link_reunion: e.target.value })}
                  data-testid="meeting-link-input" />
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={resetForm}>Cancelar</Button>
              <Button size="sm" onClick={guardar} disabled={saving} data-testid="save-meeting-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {editingId ? 'Guardar cambios' : 'Crear Reunión'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      )}

      {!loading && itemsUnificados.length === 0 && (
        <Card className="text-center py-8">
          <CardContent>
            <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay eventos programados</p>
          </CardContent>
        </Card>
      )}

      {/* Próximos */}
      {!loading && proximos.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-violet-600" />
            Próximos eventos
          </h3>
          <div className="space-y-3">
            {proximos.map(item => item._tipo === 'evento'
              ? <LinkedEventCard key={item.id} item={item} esStaff={esCoordinadorODocente} onDesvincular={() => desvincularEvento(item._linkId)} />
              : <MeetingCard key={item.id} reunion={item} esStaff={esCoordinadorODocente}
                  onEdit={() => editar(item)} onDelete={() => eliminar(item.id)}
                  onChangeEstado={(estado) => cambiarEstado(item.id, estado)}
                  onGrabacion={() => agregarGrabacion(item.id)}
                  onGenerar={() => generarLink(item.id)} generando={generandoId === item.id}
                  onAnfitrion={() => iniciarAnfitrion(item)}
                  onMiLink={() => abrirMiLink(item.id)}
                  asistenciaPanel={esCoordinadorODocente && tieneAsistenciaAuto(item) && new Date(item.fecha_hora) < new Date() && (
                    <AsistenciaZoomPanel reunion={item} spaceId={spaceId} headers={headers} onDone={fetchReuniones} />
                  )} />
            )}
          </div>
        </div>
      )}

      {/* Anteriores */}
      {!loading && anteriores.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Eventos anteriores
          </h3>
          <div className="space-y-3">
            {anteriores.map(item => item._tipo === 'evento'
              ? <LinkedEventCard key={item.id} item={item} esStaff={esCoordinadorODocente} onDesvincular={() => desvincularEvento(item._linkId)} />
              : <MeetingCard key={item.id} reunion={item} esStaff={esCoordinadorODocente}
                  onEdit={() => editar(item)} onDelete={() => eliminar(item.id)}
                  onChangeEstado={(estado) => cambiarEstado(item.id, estado)}
                  onGrabacion={() => agregarGrabacion(item.id)}
                  onGenerar={() => generarLink(item.id)} generando={generandoId === item.id}
                  onAnfitrion={() => iniciarAnfitrion(item)}
                  onMiLink={() => abrirMiLink(item.id)}
                  asistenciaPanel={esCoordinadorODocente && tieneAsistenciaAuto(item) && new Date(item.fecha_hora) < new Date() && (
                    <AsistenciaZoomPanel reunion={item} spaceId={spaceId} headers={headers} onDone={fetchReuniones} />
                  )} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MeetingCard({ reunion: r, esStaff, onEdit, onDelete, onChangeEstado, onGrabacion, onGenerar, generando, onAnfitrion, onMiLink, asistenciaPanel }) {
  const estado = r.estado_calculado || r.estado;
  const estadoCfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG.programada;
  const platCfg = PLATAFORMA_CONFIG[r.plataforma] || PLATAFORMA_CONFIG.otro;
  const enCurso = estado === 'en_curso';
  const programada = estado === 'programada';

  return (
    <Card className={`overflow-hidden ${enCurso ? 'ring-2 ring-green-400' : ''}`}
      data-testid={`meeting-card-${r.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 flex-1 min-w-0">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
              enCurso ? 'bg-green-100' : programada ? 'bg-violet-100' : 'bg-gray-100'
            }`}>
              <Video className={`w-6 h-6 ${
                enCurso ? 'text-green-600' : programada ? 'text-violet-600' : 'text-gray-400'
              }`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`${estadoCfg.color} border-0 text-xs`}>{estadoCfg.label}</Badge>
                <Badge className={`${platCfg.color} border-0 text-xs`}>{platCfg.label}</Badge>
              </div>
              <h3 className="font-semibold text-gray-900 truncate">{r.titulo}</h3>
              {r.descripcion && (
                <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{r.descripcion}</p>
              )}
              {asistenciaPanel}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatFecha(r.fecha_hora)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {r.duracion} min
                </span>
                {r.autor && (
                  <span>por {r.autor.nombre} {r.autor.apellido}</span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 flex-shrink-0">
            {esStaff && (enCurso || programada) && ['meet', 'zoom'].includes(r.plataforma) && !r.creada_automaticamente && (
              <button type="button" onClick={onGenerar} disabled={generando} data-testid={`generar-link-${r.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 text-white rounded-md text-xs font-medium hover:bg-violet-700 disabled:opacity-60 transition-colors">
                <Wand2 className="w-3.5 h-3.5" />{generando ? 'Generando…' : 'Generar link automáticamente'}
              </button>
            )}
            {esStaff && (enCurso || programada) && r.creada_automaticamente && (
              <button type="button" onClick={onAnfitrion} data-testid={`iniciar-anfitrion-${r.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 transition-colors">
                <PlayCircle className="w-3.5 h-3.5" />Iniciar como anfitrión
              </button>
            )}
            {!esStaff && (enCurso || programada) && !r.link_reunion && !r.link_personal && ['meet', 'zoom', 'teams', 'otro'].includes(r.plataforma) && (
              <span className="text-xs text-gray-500 italic" data-testid={`link-pendiente-${r.id}`}>Link pendiente</span>
            )}
            {(enCurso || programada) && r.link_personal && (
              <button type="button" onClick={onMiLink} data-testid={`join-personal-${r.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors">
                <ExternalLink className="w-3.5 h-3.5" />
                {enCurso ? 'Unirse ahora' : 'Abrir mi link'}
              </button>
            )}
            {(enCurso || programada) && r.link_reunion && (
              <a href={r.link_reunion} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                data-testid={`join-meeting-${r.id}`}>
                <ExternalLink className="w-3.5 h-3.5" />
                {enCurso ? 'Unirse ahora' : 'Abrir link'}
              </a>
            )}
            {r.grabacion_url && (
              <a href={r.grabacion_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-md text-xs font-medium hover:bg-violet-200 transition-colors"
                data-testid={`recording-${r.id}`}>
                <Film className="w-3.5 h-3.5" />
                Ver grabación
              </a>
            )}
            {esStaff && (
              <div className="flex gap-1 justify-end">
                {estado === 'finalizada' && !r.grabacion_url && (
                  <button onClick={onGrabacion} className="text-gray-400 hover:text-violet-600 p-1" title="Agregar grabación">
                    <MonitorPlay className="w-4 h-4" />
                  </button>
                )}
                {(programada || enCurso) && (
                  <button onClick={onEdit} className="text-gray-400 hover:text-blue-600 p-1" title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                {programada && (
                  <button onClick={() => onChangeEstado('cancelada')} className="text-gray-400 hover:text-red-500 p-1" title="Cancelar">
                    <X className="w-4 h-4" />
                  </button>
                )}
                <button onClick={onDelete} className="text-gray-300 hover:text-red-500 p-1" title="Eliminar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function LinkedEventCard({ item, esStaff, onDesvincular }) {
  const programada = item._estado === 'programada' || item._estado === 'en_curso';
  const estadoCfg = ESTADO_CONFIG[item._estado] || ESTADO_CONFIG.programada;

  const fechaDisplay = item._fechaEvento
    ? new Date(item._fechaEvento + 'T12:00:00').toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', weekday: 'long', day: '2-digit', month: 'long' })
    : null;

  return (
    <Card className="overflow-hidden" data-testid={`linked-event-${item.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3 flex-1 min-w-0">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${programada ? 'bg-amber-100' : 'bg-gray-100'}`}>
              <Calendar className={`w-6 h-6 ${programada ? 'text-amber-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`${estadoCfg.color} border-0 text-xs`}>{estadoCfg.label}</Badge>
                <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Evento público</Badge>
                {item._inscripcionesAbiertas && (
                  <Badge className="bg-green-100 text-green-700 border-0 text-xs">Inscripciones abiertas</Badge>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 truncate">{item._nombre}</h3>
              {item._descripcion && (
                <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{item._descripcion}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                {fechaDisplay && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {fechaDisplay}
                  </span>
                )}
                {item._horaEvento && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item._horaEvento} hs
                  </span>
                )}
                {item._lugar && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {item._lugar}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <a href={`/eventos/${item._eventoId}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 transition-colors"
              data-testid={`view-event-${item.id}`}>
              <ExternalLink className="w-3.5 h-3.5" />
              Ver evento
            </a>
            {esStaff && (
              <button onClick={onDesvincular} className="text-gray-300 hover:text-red-500 p-1 self-end" title="Desvincular">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
